import React, { Component } from "react";
import { Spin, Icon, Modal } from 'antd';
import Login from 'views/NewLogin';
import _authService from 'modules/AuthService';
import _notification from 'modules/notification';
import PublicFooter from "../components/Footer/PublicFooter";
import Text, { resolveText } from 'components/Text/Text';
import { getCurrentLocale, getCurrentDisplayMode } from 'modules/LanguageContext';
import _branding from 'modules/branding';
import { PROJECT_LOGO } from '../constants/constants';
// Phase C.6.1 (Candidate Homepage & Login Modernization): wires the homepage to the existing,
// already-anonymous-accessible Publication foundation (Phase C.1) — this file previously never
// called this service at all (confirmed absent in Phase C.6's inventory). No backend/schema change;
// only a new frontend consumer of an already-built API. Type labels are centralized in
// modules/i18n/dictionary.js's `publications` namespace (same architecture as every other migrated
// Candidate string — not a local/duplicate translation mechanism).
import _PublicationService from 'modules/PublicationService';

function tr(key) {
  return resolveText(key, getCurrentLocale(), getCurrentDisplayMode());
}

// Groups the flat candidate-facing publication list into the 2 sections the C.6 blueprint defines.
// "Important Documents & Notifications" vs. "Publications" — grouping only, the underlying
// PublicationType values and lifecycle are entirely unchanged (Phase C.1).
const IMPORTANT_DOCUMENTS_TYPES = ['RecruitmentAdvertisement', 'CandidateUserGuide', 'ImportantInstructions', 'OtherNotice'];
const PUBLICATIONS_LIST_TYPES = ['EligibleCandidatesExamination', 'EligibleCandidatesInterview', 'SelectedAndWaitingList'];

// Maps a PublicationType value (backend constant, Phase C.1 — see PublicationService.cs) to its
// dictionary.js `publications.*` key. Falls back to the raw type string if an admin ever stores an
// unrecognized type — matching Text.jsx's own "missing key renders the key" fallback philosophy.
const PUBLICATION_TYPE_KEY = {
  RecruitmentAdvertisement: 'publications.recruitmentAdvertisement',
  CandidateUserGuide: 'publications.candidateUserGuide',
  ImportantInstructions: 'publications.importantInstructions',
  OtherNotice: 'publications.otherNotice',
  EligibleCandidatesExamination: 'publications.eligibleCandidatesExamination',
  EligibleCandidatesInterview: 'publications.eligibleCandidatesInterview',
  SelectedAndWaitingList: 'publications.selectedAndWaitingList'
};

function publicationTypeLabel(publicationType) {
  const key = PUBLICATION_TYPE_KEY[publicationType];
  return key ? tr(key) : publicationType;
}

// Phase C.6.2.3 (§10/§11 spacing investigation): CKEditor serializes an "empty" editor as
// `<p><br></p>` or `<p>&nbsp;</p>`, not an empty string — so `resData.LandingPage_SecondaryTable &&
// (...)` was truthy and rendering a visible (if textless) block, which was most of the "excessive
// gap" the Product Owner flagged between the hero and Important Dates/Need Help. This treats those
// CKEditor empty-content serializations as genuinely absent, same as a real empty string.
function hasRealContent(html) {
  if (!html) return false;
  const stripped = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').trim();
  return stripped.length > 0;
}

const antIcon = <Icon type="loading" style={{ fontSize: 30 }} spin />;

// Phase C.6.2 (Candidate Homepage Visual Modernization): a small, reusable document-row primitive —
// used by both the "Important Documents & Notifications" and "Publications" sections so the two
// modernized lists share one consistent visual pattern instead of two slightly different ones.
function DocumentRow({ href, onClick, icon, children }) {
  const content = (
    <>
      <span className="ent-doc-row-icon"><Icon type={icon || 'file-pdf'} /></span>
      <span className="ent-doc-row-title">{children}</span>
      <span className="ent-doc-row-action"><Text k="homepage.viewAction" /> <Icon type="right" /></span>
    </>
  );
  if (href) {
    return <a className="ent-doc-row" href={href} target="_blank" rel="noopener noreferrer">{content}</a>;
  }
  // No href means this row triggers an action (the Important Instructions modal) rather than
  // navigating — a real <button>, not a clickable <div role="button">, per this phase's own
  // accessibility requirement. Native button semantics/keyboard handling (Enter/Space) come for free.
  return (
    <button type="button" className="ent-doc-row ent-doc-row-button" onClick={onClick}>
      {content}
    </button>
  );
}

class LandingPage_new_one extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      resData: {},
      // Phase C.6.1: Publication foundation (Phase C.1) wiring — additive state only.
      publications: [],
      publicationsLoaded: false,
      // Phase C.6.2: Important Notices modal visibility (was a plain top-level `state` field
      // assignment before — see the historical note this replaces, just below).
      visible: false
    }
    // Phase C.6.2.1 (§16): the new "Important Links" card triggers PublicFooter's own existing
    // Drawer (showDrawer()/getPortalConfigurationByKey()) instead of duplicating that mechanism —
    // a ref is the minimal way to call it from a sibling location in the tree.
    this.footerRef = React.createRef();
  }

  showModal = () => {
    this.setState({
      visible: true,
    });
  };
  handleCancel = () => {
    this.setState({
      visible: false,
    });
  };

  componentDidMount() {
    this.getInfo();
    this.getPublications();
    // Phase C.6.2: the hero band now renders branding (logo/org name) directly in this component,
    // which previously lived only in PublicHeader.jsx (suppressed on the homepage since Sprint
    // 2.6.7C — see that file's own comment). PublicHeader.jsx already fixed a real timing bug here
    // (index.js's own applyBranding() call at app bootstrap is fire-and-forget and racing against
    // first render, so a hard page load can render the fallback org name/logo and never update): it
    // re-fires the same idempotent applyBranding() on mount and forces a re-render once it resolves.
    // Moving branding display into this component without carrying that same fix over would silently
    // reintroduce the bug here — so it's repeated verbatim, not a new mechanism.
    _branding.applyBranding().then(() => this.forceUpdate());
  }

  // Phase C.6.1: consumes the existing, already-anonymous Publication API (Phase C.1) — read-only,
  // no publication CRUD/lifecycle/schema touched. Best-effort: a failure here must not block the rest
  // of the homepage (identical resilience pattern to getInfo() above).
  getPublications = async () => {
    try {
      // Real defect fix (28 Aug 2026): the previous cap of 10 assumed at most one published document
      // per PublicationType (7 types) — true when this comment was written, but multi-Post recruitment
      // (First/Final/Interview/Selected&Waiting each published per-Post, e.g. Clerk+Peon+Driver) means
      // the real count is Posts × 4 stages, which already exceeds 10 with just 3 Posts. Confirmed live:
      // "Selected & Waiting List - Peon/Driver" were silently missing from the homepage once a 3rd Post
      // was added. Raised well above any realistic near-term Post count; the API's own maxCount
      // parameter already supports this, no backend change involved.
      const response = await _PublicationService.getActivePublications(50);
      if (response && response.status === 200 && Array.isArray(response.data)) {
        this.setState({ publications: response.data, publicationsLoaded: true });
      } else {
        this.setState({ publicationsLoaded: true });
      }
    } catch (e) {
      this.setState({ publicationsLoaded: true });
    }
  }

  getInfo = async (ev) => {
    await _authService.clearSessionHist();

    this.setState({ isLoading: true });
    try {
      const response = await _authService.cachePortalConfiguration();
      if (response.status == 200 && response.data) {
        this.setState({ isLoading: false, resData: { ...response.data } })

      }
      else {
        this.setState({ isLoading: false });
        _notification.showMessage('error', response.data.Message);
      }
    } catch (e) {
      this.setState({ isLoading: false })

    }

  }

  render() {

    if (this.state.isLoading)

      return <Spin indicator={antIcon} tip="&nbsp;Please wait..." />;

    const { resData, publications, publicationsLoaded } = this.state;
    // Phase C.6.2.1 (§2 source trace): PortalConfiguration.Portal_Logo (Admin → Settings → Portal
    // Config → Global Portal Configuration → Portal Logo) is the field the Product Owner actually
    // edits, and it is ALREADY what the authenticated Candidate area renders (PrivateHeader.jsx reads
    // localStorage['Portal_Logo'] directly). C.6.2 had this backwards — it asked
    // `_branding.getLogoUrl(resData.Portal_Logo || PROJECT_LOGO)`, which lets the EnterpriseConfiguration
    // Branding-category LogoUrl (a separate, DB-seeded, no-Admin-UI value) win whenever it's set, so a
    // new Portal_Logo upload could never appear on the public homepage. Portal_Logo now takes priority,
    // exactly matching the source that's proven to work in the authenticated area; `_branding`'s value
    // (then PROJECT_LOGO) is only a fallback for a tenant that hasn't uploaded a Portal_Logo yet.
    const logoUrl = resData.Portal_Logo || _branding.getLogoUrl(PROJECT_LOGO);
    // Organization Name source trace (§3): this still comes from EnterpriseConfiguration's
    // Branding-category OrganizationName field (via _branding.getOrganizationName()) — genuinely
    // tenant-configurable data, not a hardcoded string, so per the governing document's own rule
    // ("if already tenant-configurable, reuse it") this is intentionally NOT changed. Documented
    // limitation (no Admin UI exists to edit it today): see the C.6.2.1 report §D/§Q.
    const orgName = _branding.getOrganizationName("Candidate Recruitment Portal");
    const importantDocuments = publications.filter(p => IMPORTANT_DOCUMENTS_TYPES.includes(p.PublicationType));
    const publicationsList = publications.filter(p => PUBLICATIONS_LIST_TYPES.includes(p.PublicationType));

    return (<>

      <div className="candidate ent-home">
        {/* Phase C.6.2 (Candidate Homepage Visual Modernization): a single consolidated hero band
            replaces the old plain CMS banner + disconnected sidebar login panel. Organization
            branding (logo/name — tenant-configurable, §7) and the CMS-authored portal title
            (LandingPage_Header — already English-only as of Phase C.6.1) now live in one visual
            unit, with the 2 primary actions (New Registration / Registered Candidate Login,
            unchanged component/logic — see NewLogin.jsx) directly beneath. No component here is
            hardcoded to any one tenant: logo/org-name/title are all configuration-driven. */}
        {/* Phase C.6.2.3 (§3/§6): the Product Owner confirmed BOTH configured text values must stay —
            OrganizationName (Branding config, "National Bank PO Recruitment 2026" — the dominant
            recruitment identity, Level 1) and LandingPage_Header (Portal Config, "Online Application
            Portal" — the subordinate portal descriptor, Level 2). Previously these rendered as two
            visually independent, separately-centered elements with the logo above them; now the logo
            anchors both as one identity lockup (DOM order: logo → recruitment identity → portal
            descriptor → CTAs, per §19's accessibility requirement), with hierarchy expressed through
            size/weight (CSS), not by favoring one data source over the other. */}
        {/* Phase C.6.2.5 (Enterprise Hero/Header Composition Refinement): a genuine 3-zone enterprise
            identity bar — Zone 1 (logo) + Zone 2 (recruitment identity/portal descriptor) grouped on
            the left, Zone 3 (candidate actions) pinned right on the SAME horizontal band via
            `.ent-home-hero-band`'s space-between — replacing C.6.2.3/C.6.2.4's single centered group
            with CTAs stacked below it. DOM order unchanged (logo → identity → descriptor → CTAs, per
            the accessibility requirement every prior phase has kept); only the CSS layout changed.
            Both configured values still render from their existing, untouched sources. */}
        <header className="ent-home-hero">
          <div className="ent-home-hero-band">
            <div className="ent-home-hero-lockup">
              <img src={logoUrl} alt={orgName} className="ent-home-hero-logo" />
              <div className="ent-home-hero-identity">
                <div className="ent-home-hero-org-name">{orgName}</div>
                <div className="ent-home-hero-title" dangerouslySetInnerHTML={{ __html: resData.LandingPage_Header }}></div>
              </div>
            </div>
            <div className="ent-home-hero-actions">
              {/* Recruitment Candidate-List Publication Modernization phase: IsRegistrationClosed folds
                  in both the manual admin toggle and the new configured cutoff date/time — see
                  Registration_new.jsx's getInfo() for the equivalent server-driven check. */}
              <Login image={resData.Portal_Logo} registrationDisable={resData.IsRegistrationClosed} />
            </div>
          </div>
        </header>

        <main className="ent-home-main ent-container">
          {/* Sprint 2.6.7B: PortalConfiguration.LandingPage_SecondaryTable is a reserved, tenant-
              configured advertisement/notice area — conditional on content, never a fixed blank
              region (already satisfies §12's "no dedicated advertisement whitespace" requirement;
              unchanged data source/behavior in this phase). */}
          {hasRealContent(resData.LandingPage_SecondaryTable) && (
            <div className="ent-home-advertisement" dangerouslySetInnerHTML={{ __html: resData.LandingPage_SecondaryTable }}></div>
          )}

          <div className="ent-home-grid">
            <div className="ent-home-col-main">
              {/* Phase C.6.2.1 (§14): the Product Owner revised the information hierarchy — Important
                  Dates is more important to candidates at first glance than Important Documents, so it
                  now renders first. Same data source/behavior as C.6.2, only the position moved. */}
              <section className="ent-home-card ent-important-dates">
                <h2 className="ent-home-card-title"><Icon type="calendar" /> <Text k="homepage.importantDatesHeading" /></h2>
                <div className="ent-important-dates-content" dangerouslySetInnerHTML={{ __html: resData.LandingPage_Content }}></div>
              </section>

              {/* Important Documents & Notifications — modernized document-row list (§9). The 2
                  pre-existing static items (User Guide external link, Important Instructions modal)
                  are preserved unchanged; Admin-published documents (Phase C.1) render additively
                  alongside them, never replacing them. */}
              <section className="ent-home-card">
                <h2 className="ent-home-card-title"><Text k="landingPage.importantDocumentsHeading" /></h2>
                <div className="ent-doc-list">
                  <DocumentRow href="../userGuide.pdf" icon="read"><Text k="landingPage.userGuideLink" /></DocumentRow>
                  <DocumentRow onClick={this.showModal} icon="notification"><Text k="landingPage.importantInstructionsLink" /></DocumentRow>
                  {importantDocuments.map(p => (
                    <DocumentRow key={p.Id} href={p.DocumentAccessUrl} icon="file-pdf">
                      {p.Title || publicationTypeLabel(p.PublicationType)}
                    </DocumentRow>
                  ))}
                </div>
              </section>

              {/* Publications (§10) — unchanged Phase C.1 API/lifecycle; modernized document-row
                  presentation, same primitive as Important Documents above for visual consistency.
                  Compact, intentional empty state per §10 — never a large blank table. */}
              <section className="ent-home-card">
                <h2 className="ent-home-card-title"><Icon type="solution" /> <Text k="publications.publicationsHeading" /></h2>
                {publicationsList.length > 0 ? (
                  <div className="ent-doc-list">
                    {publicationsList.map(p => (
                      <DocumentRow key={p.Id} href={p.DocumentAccessUrl} icon="file-pdf">
                        {p.Title || publicationTypeLabel(p.PublicationType)}
                      </DocumentRow>
                    ))}
                  </div>
                ) : (
                  publicationsLoaded && (
                    <p className="ent-empty-state"><Text k="publications.noPublicationsMessage" /></p>
                  )
                )}
              </section>
            </div>

            {/* Need Help? (§13) — same existing Portal Config data source (LandingPage_Footer,
                already carrying the tenant's Helpline Number/Email today) in a compact, professional
                card instead of the old full-width plain text block. No bank-specific value is
                hardcoded here — the field's own configured content is rendered as-is. */}
            <aside className="ent-home-col-side">
              <section className="ent-home-card ent-help-card">
                <h2 className="ent-home-card-title"><Icon type="question-circle" /> <Text k="homepage.needHelpHeading" /></h2>
                <div className="ent-help-content" dangerouslySetInnerHTML={{ __html: resData.LandingPage_Footer }}></div>
              </section>

              {/* Phase C.6.2.1 (§16): a standalone card for the 5 legal/informational links — the
                  Product Owner explicitly requires these NOT live inside the Need Help card. Reuses
                  PublicFooter's own existing Drawer/showDrawer()/getPortalConfigurationByKey()
                  mechanism via a ref (see constructor) — no new route, modal, or content source. */}
              <section className="ent-home-card ent-links-card">
                <h2 className="ent-home-card-title"><Icon type="link" /> <Text k="homepage.importantLinksHeading" /></h2>
                <div className="ent-doc-list">
                  <button type="button" className="ent-doc-row ent-doc-row-button" onClick={() => this.footerRef.current.showDrawer("refundpolicies")}>
                    <span className="ent-doc-row-title"><Text k="homepage.refundPoliciesLink" /></span>
                    <span className="ent-doc-row-action"><Icon type="right" /></span>
                  </button>
                  <button type="button" className="ent-doc-row ent-doc-row-button" onClick={() => this.footerRef.current.showDrawer("contactus")}>
                    <span className="ent-doc-row-title"><Text k="homepage.contactUsLink" /></span>
                    <span className="ent-doc-row-action"><Icon type="right" /></span>
                  </button>
                  <button type="button" className="ent-doc-row ent-doc-row-button" onClick={() => this.footerRef.current.showDrawer("aboutus")}>
                    <span className="ent-doc-row-title"><Text k="homepage.aboutUsLink" /></span>
                    <span className="ent-doc-row-action"><Icon type="right" /></span>
                  </button>
                  <button type="button" className="ent-doc-row ent-doc-row-button" onClick={() => this.footerRef.current.showDrawer("privacypolicy")}>
                    <span className="ent-doc-row-title"><Text k="homepage.privacyPolicyLink" /></span>
                    <span className="ent-doc-row-action"><Icon type="right" /></span>
                  </button>
                  <button type="button" className="ent-doc-row ent-doc-row-button" onClick={() => this.footerRef.current.showDrawer("termsandconditions")}>
                    <span className="ent-doc-row-title"><Text k="homepage.termsConditionsLink" /></span>
                    <span className="ent-doc-row-action"><Icon type="right" /></span>
                  </button>
                </div>
              </section>
            </aside>
          </div>
        </main>

        {/* hideLinkBar: the 5 links now live in the Important Links card above — not duplicated here.
            The compatibility statement (and the Drawer itself, which the ref above calls into) stay
            exactly as PublicFooter already renders them for every other public page. */}
        <PublicFooter ref={this.footerRef} hideLinkBar />
      </div>

      <Modal
        title="Important Notices"
        visible={this.state.visible}
        onCancel={this.handleCancel}
        footer={null}
        className="important-notices-modal modal-global"
      >
        <div>
          <div className="pr-4 pl-4 pt-4">
            <div dangerouslySetInnerHTML={{ __html: this.state.resData.LandingPage_Suchana }}></div>
          </div>
        </div>
      </Modal>
    </>);
  }
}

export default LandingPage_new_one;
